export class Renderer {
  constructor(ctx, width, height) {
    this.ctx = ctx;
    this.width = width;
    this.height = height;
    this.phase = 0; // For wave animation
  }

  clear() {
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  render(state) {
    this.phase += 0.05; // Animate waves

    // Draw player frequency wave (THICKER, more visible)
    this.drawSineWave(
      state.playerFrequency,
      '#00f3ff',
      6,  // Thicker line
      1.0,
      this.phase,
      0, // Start from left
      true // Player wave
    );

    // Draw monster waves and orbs
    state.monsters.forEach(monster => {
      // Calculate wave phase based on position
      const monsterPhase = (monster.x / this.width) * Math.PI * 2;

      // Draw monster wave (from right) - THICKER
      this.drawSineWave(
        monster.frequency,
        monster.color,
        4,  // Thicker
        0.8, // More visible
        this.phase + monsterPhase,
        monster.x / this.width, // Start from monster position
        false // Not player
      );

      // Draw monster orb
      this.drawMonsterOrb(monster);

      // Draw lock indicator if matched
      if (monster.isLocked) {
        this.drawLockProgress(monster);
      }
    });

    // Draw match effect
    if (state.matchState) {
      this.drawMatchEffect(state.matchState);
    }

    // Draw target frequency indicator and direction
    if (state.monsters.length > 0) {
      this.drawTargetFrequency(state);
      this.drawDirectionIndicator(state);
    }
  }

  drawSineWave(frequency, color, lineWidth, opacity, phase, startX = 0, isPlayer = false) {
    const ctx = this.ctx;
    const amplitude = 100; // BIGGER amplitude for clearer waves
    const centerY = this.height / 2;

    // Normalize frequency to wavelength (visual mapping)
    // Make wavelength differences MORE visible
    const wavelength = 0.05 / (frequency / 400); // More pronounced difference

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = opacity;
    ctx.shadowBlur = 25;
    ctx.shadowColor = color;

    // Add extra glow for player wave
    if (isPlayer) {
      ctx.shadowBlur = 35;
    }

    ctx.beginPath();
    const startPixel = startX * this.width;

    for (let x = startPixel; x < this.width; x += 1) {
      const y = centerY + Math.sin((x - startPixel) * wavelength + phase) * amplitude;

      if (x === startPixel) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.stroke();
    ctx.restore();
  }

  drawMonsterOrb(monster) {
    const ctx = this.ctx;
    const radius = 20;

    ctx.save();

    // Outer glow
    const gradient = ctx.createRadialGradient(
      monster.x, monster.y, 0,
      monster.x, monster.y, radius * 2
    );
    gradient.addColorStop(0, monster.color);
    gradient.addColorStop(0.5, monster.color + '88');
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(monster.x, monster.y, radius * 2, 0, Math.PI * 2);
    ctx.fill();

    // Core orb
    ctx.fillStyle = monster.color;
    ctx.shadowBlur = 15;
    ctx.shadowColor = monster.color;
    ctx.beginPath();
    ctx.arc(monster.x, monster.y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  drawLockProgress(monster) {
    const ctx = this.ctx;
    const progress = 1 - (monster.currentHealth / monster.health);
    const barWidth = 60;
    const barHeight = 6;
    const x = monster.x - barWidth / 2;
    const y = monster.y - 40;

    ctx.save();

    // Background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.fillRect(x, y, barWidth, barHeight);

    // Progress
    ctx.fillStyle = '#00f3ff';
    ctx.shadowBlur = 10;
    ctx.shadowColor = '#00f3ff';
    ctx.fillRect(x, y, barWidth * progress, barHeight);

    ctx.restore();
  }

  drawMatchEffect(matchState) {
    const ctx = this.ctx;
    const centerY = this.height / 2;

    // Draw pulsing indicator at match points
    matchState.monsters.forEach(monster => {
      const pulseRadius = 30 + Math.sin(this.phase * 2) * 10;

      ctx.save();
      ctx.strokeStyle = '#00f3ff';
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.6;

      ctx.beginPath();
      ctx.arc(monster.x, centerY, pulseRadius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    });

    // Draw "LOCKED" text
    if (matchState.monsters.length > 0) {
      const monster = matchState.monsters[0];
      ctx.save();
      ctx.fillStyle = '#00f3ff';
      ctx.font = '14px Courier New';
      ctx.textAlign = 'center';
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#00f3ff';
      ctx.fillText('LOCKED', monster.x, monster.y + 60);
      ctx.restore();
    }
  }

  drawTargetFrequency(state) {
    const ctx = this.ctx;

    // Calculate target frequency (same logic as game)
    const typeGroups = {};
    state.monsters.forEach(m => {
      if (!typeGroups[m.type]) typeGroups[m.type] = [];
      typeGroups[m.type].push(m);
    });

    let targetFreq = 0;
    if (Object.keys(typeGroups).length === 1) {
      const allMonsters = Object.values(typeGroups)[0];
      targetFreq = allMonsters.reduce((sum, m) => sum + m.frequency, 0) / allMonsters.length;
    } else {
      const closest = state.monsters.reduce((prev, curr) => curr.x < prev.x ? curr : prev);
      targetFreq = closest.frequency;
    }

    // Draw target frequency at top
    ctx.save();
    ctx.fillStyle = '#ff6b35';
    ctx.font = '20px Courier New';
    ctx.textAlign = 'center';
    ctx.shadowBlur = 10;
    ctx.shadowColor = '#ff6b35';
    ctx.fillText(`TARGET: ${Math.round(targetFreq)} Hz`, this.width / 2, 40);
    ctx.restore();
  }

  drawDirectionIndicator(state) {
    const ctx = this.ctx;

    // Calculate target frequency
    const typeGroups = {};
    state.monsters.forEach(m => {
      if (!typeGroups[m.type]) typeGroups[m.type] = [];
      typeGroups[m.type].push(m);
    });

    let targetFreq = 0;
    if (Object.keys(typeGroups).length === 1) {
      const allMonsters = Object.values(typeGroups)[0];
      targetFreq = allMonsters.reduce((sum, m) => sum + m.frequency, 0) / allMonsters.length;
    } else {
      const closest = state.monsters.reduce((prev, curr) => curr.x < prev.x ? curr : prev);
      targetFreq = closest.frequency;
    }

    const diff = targetFreq - state.playerFrequency;
    const tolerance = 20;

    if (Math.abs(diff) > tolerance) {
      const arrow = diff > 0 ? '▲ HIGHER' : '▼ LOWER';
      const color = diff > 0 ? '#00f3ff' : '#ff6b35';

      ctx.save();
      ctx.fillStyle = color;
      ctx.font = 'bold 24px Courier New';
      ctx.textAlign = 'center';
      ctx.shadowBlur = 15;
      ctx.shadowColor = color;
      ctx.fillText(arrow, this.width / 2, 80);
      ctx.restore();
    }
  }
}
